import Phaser from 'phaser';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import {
  byggCha,
  byggKonfetti,
  byggSpark,
  byggWhoosh,
  defaultCount,
  whooshVarighet,
  type PartikkelPreset,
} from '@/ui/partikkelKonfig';

const PARTICLE_KEY = 'particle-dot';

function sikrePartikkelTextur(scene: Phaser.Scene): string {
  if (scene.textures.exists(PARTICLE_KEY)) return PARTICLE_KEY;
  const g = scene.add.graphics();
  g.fillStyle(0xffffff, 1);
  g.fillCircle(8, 8, 8);
  g.generateTexture(PARTICLE_KEY, 16, 16);
  g.destroy();
  return PARTICLE_KEY;
}

type Emitter = Phaser.GameObjects.Particles.ParticleEmitter;

export type PartikkelKonfig = {
  preset: PartikkelPreset;
  x: number;
  y: number;
  count?: number;
  toX?: number;
};

export class Particles {
  private aktiveEmittere: Emitter[] = [];

  constructor(private scene: Phaser.Scene) {}

  konfetti(scene: Phaser.Scene, x: number, y: number, count = 30): void {
    void scene;
    if (foretrekkerRedusertBevegelse()) return;
    const tex = sikrePartikkelTextur(this.scene);
    const konfig = byggKonfetti(count);
    const emitter = this.scene.add.particles(x, y, tex, konfig);
    emitter.setDepth(950);
    emitter.explode(count);
    this.registrer(emitter, 2400);
  }

  spark(scene: Phaser.Scene, x: number, y: number): void {
    void scene;
    if (foretrekkerRedusertBevegelse()) return;
    const tex = sikrePartikkelTextur(this.scene);
    const konfig = byggSpark();
    const emitter = this.scene.add.particles(x, y, tex, konfig);
    emitter.setDepth(950);
    emitter.explode(konfig.quantity);
    this.registrer(emitter, 900);
  }

  cha(scene: Phaser.Scene, x: number, y: number): void {
    void scene;
    if (foretrekkerRedusertBevegelse()) return;
    const tex = sikrePartikkelTextur(this.scene);
    const konfig = byggCha();
    const emitter = this.scene.add.particles(x, y, tex, konfig);
    emitter.setDepth(950);
    emitter.explode(konfig.quantity);
    this.registrer(emitter, 1900);
  }

  whoosh(scene: Phaser.Scene, fromX: number, toX: number, y: number): void {
    void scene;
    if (foretrekkerRedusertBevegelse()) return;
    const tex = sikrePartikkelTextur(this.scene);
    const retning = toX >= fromX ? 1 : -1;
    const konfig = byggWhoosh(retning);
    const emitter = this.scene.add.particles(fromX, y, tex, konfig);
    emitter.setDepth(950);
    const varighet = whooshVarighet(fromX, toX);
    this.scene.time.delayedCall(varighet, () => emitter.stop());
    this.registrer(emitter, varighet + 1100);
  }

  emit(konfig: PartikkelKonfig): void {
    switch (konfig.preset) {
      case 'konfetti':
        this.konfetti(this.scene, konfig.x, konfig.y, konfig.count ?? defaultCount('konfetti'));
        break;
      case 'spark':
        this.spark(this.scene, konfig.x, konfig.y);
        break;
      case 'cha':
        this.cha(this.scene, konfig.x, konfig.y);
        break;
      case 'whoosh':
        this.whoosh(this.scene, konfig.x, konfig.toX ?? konfig.x + 400, konfig.y);
        break;
    }
  }

  destroy(): void {
    for (const e of this.aktiveEmittere) {
      try {
        e.destroy();
      } catch {
        // ignore
      }
    }
    this.aktiveEmittere = [];
  }

  private registrer(emitter: Emitter, levetidMs: number): void {
    this.aktiveEmittere.push(emitter);
    this.scene.time.delayedCall(levetidMs, () => {
      try {
        emitter.destroy();
      } catch {
        // ignore
      }
      this.aktiveEmittere = this.aktiveEmittere.filter((e) => e !== emitter);
    });
  }
}

export { defaultCount as lagDefaultCount } from '@/ui/partikkelKonfig';
export type { PartikkelPreset };
